/**
 * Языки приложения и правило выбора (план `docs/i18n-plan.md`, разделы 3–4).
 *
 * Код языка — две буквы, один на все слои: веб, сервер, ресурсы Android. Список один,
 * из него берутся переключатели, sitemap и проверки словарей.
 */

export const LANGS = ['ru', 'en', 'zh', 'es'] as const
export type Lang = (typeof LANGS)[number]

/** Язык, когда определить не вышло (решение заказчика 3). */
export const DEFAULT_LANG: Lang = 'en'

/** Ключ хранилища с явным выбором человека. Не под префиксом малыша: язык про человека. */
export const LANG_KEY = 'moonwake.lang'

/** Названия на самих языках — в переключателе язык пишется так, как его прочтёт носитель. */
export const LANG_NAMES: Record<Lang, string> = { ru: 'Русский', en: 'English', zh: '中文', es: 'Español' }

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value)
}

/**
 * Локаль для `Intl`: форматы дат и чисел. Английский — британский: там 24 часа и дата
 * «день месяц», как у всех остальных; региональных исключений нет (решение 11).
 */
export function localeOf(lang: Lang): string {
  return { ru: 'ru', en: 'en-GB', zh: 'zh-CN', es: 'es' }[lang]
}

/** `zh-TW` → `zh`, `es-MX` → `es`, `ru-KZ` → `ru`; неизвестный тег — null. */
export function langFromTag(tag: string): Lang | null {
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0] ?? ''
  return isLang(primary) ? primary : null
}

/** Язык из адреса сайта: `/ru`, `/ru/privacy` → ru. Корень и `/app` языка не несут. */
export function langFromPath(pathname: string): Lang | null {
  const first = pathname.split('/')[1] ?? ''
  return isLang(first) ? first : null
}

/** Адрес сайта на языке: английский живёт на корне (решение 8). */
export function sitePath(lang: Lang, rest = ''): string {
  return lang === 'en' ? `/${rest}` : `/${lang}${rest === '' ? '' : `/${rest}`}`
}

/**
 * Выбор языка по убыванию приоритета: явный выбор на устройстве, язык аккаунта, адрес
 * на сайте, языки браузера или системы, иначе английский (план, раздел 4.1).
 */
export function detectLang(input: {
  stored?: string | null
  account?: string | null
  path?: string
  tags?: readonly string[]
}): Lang {
  if (isLang(input.stored)) return input.stored
  if (isLang(input.account)) return input.account
  const fromPath = input.path === undefined ? null : langFromPath(input.path)
  if (fromPath !== null) return fromPath
  for (const tag of input.tags ?? []) {
    const lang = langFromTag(tag)
    if (lang !== null) return lang
  }
  return DEFAULT_LANG
}
